/**
 * Maps agent tool execution names to human-readable descriptions.
 * Used by ToolExecutionCard and Dashboard activity feed.
 */

interface ToolMapping {
  label: string;
  icon: string;
  getDetail: (input?: Record<string, unknown> | null, output?: Record<string, unknown> | null) => string;
}

const TOOL_MAPPINGS: Record<string, ToolMapping> = {
  place_supplier_order: {
    label: 'Placed supplier order',
    icon: 'Truck',
    getDetail: (input, output) => {
      const items = (output?.item_count ?? (Array.isArray(input?.items) ? input.items.length : null)) || '';
      return items ? `${items} item(s)` : '';
    },
  },
  get_order_details: {
    label: 'Retrieved order details',
    icon: 'FileText',
    getDetail: (input) => {
      const orderId = input?.order_id;
      return orderId ? `Order ${String(orderId).slice(0, 8)}...` : '';
    },
  },
  check_order_status: {
    label: 'Checked order status',
    icon: 'Search',
    getDetail: (input) => {
      const ref = input?.supplier_order_ref;
      return ref ? `Ref: ${String(ref)}` : '';
    },
  },
  check_inventory: {
    label: 'Checked inventory',
    icon: 'Package',
    getDetail: (input, output) => {
      const sku = input?.sku;
      const available = output?.available;
      if (sku && available !== undefined) return `${sku}: ${available} available`;
      if (sku) return `SKU: ${sku}`;
      return '';
    },
  },
  get_supplier_products: {
    label: 'Listed supplier products',
    icon: 'List',
    getDetail: (_input, output) => {
      const count = output?.count ?? (Array.isArray(output?.products) ? output.products.length : null);
      return count ? `${count} product(s)` : '';
    },
  },
  search_supplier_catalog: {
    label: 'Searched supplier catalog',
    icon: 'Search',
    getDetail: (input) => {
      const query = input?.query;
      return query ? `"${String(query)}"` : '';
    },
  },
  create_product_draft: {
    label: 'Created product draft',
    icon: 'Plus',
    getDetail: (input, output) => {
      const name = output?.product_name ?? input?.name;
      return name ? String(name) : '';
    },
  },
  publish_product: {
    label: 'Published product',
    icon: 'CheckCircle',
    getDetail: (input) => {
      const id = input?.product_id;
      return id ? `Product ${String(id).slice(0, 8)}...` : '';
    },
  },
  update_price: {
    label: 'Updated price',
    icon: 'DollarSign',
    getDetail: (input) => {
      const oldPrice = input?.old_price ?? input?.current_price;
      const newPrice = input?.new_price ?? input?.price;
      if (oldPrice && newPrice) return `$${oldPrice} → $${newPrice}`;
      if (newPrice) return `Set to $${newPrice}`;
      return '';
    },
  },
  evaluate_publish_rules: {
    label: 'Evaluated publish rules',
    icon: 'Shield',
    getDetail: (_input, output) => {
      const shouldPublish = output?.should_publish;
      return shouldPublish !== undefined ? (shouldPublish ? 'Approved' : 'Rejected') : '';
    },
  },
  generate_product_content: {
    label: 'Generated product content',
    icon: 'Sparkles',
    getDetail: (input) => {
      const name = input?.product_name ?? input?.name;
      return name ? String(name) : '';
    },
  },
  send_message: {
    label: 'Sent notification',
    icon: 'MessageSquare',
    getDetail: (input) => {
      const msg = input?.message;
      return msg ? String(msg).slice(0, 60) : '';
    },
  },
  escalate: {
    label: 'Escalated to admin',
    icon: 'AlertTriangle',
    getDetail: (input) => {
      const reason = input?.reason;
      return reason ? String(reason).slice(0, 80) : '';
    },
  },
  coordinate_supplier_return: {
    label: 'Coordinated supplier return',
    icon: 'RotateCcw',
    getDetail: (input) => {
      const returnId = input?.return_request_id;
      return returnId ? `Return ${String(returnId).slice(0, 8)}...` : '';
    },
  },
  scrape_webpage: {
    label: 'Scraped webpage',
    icon: 'Globe',
    getDetail: (input) => {
      const url = input?.url;
      return url ? String(url).replace(/^https?:\/\//, '').slice(0, 60) : '';
    },
  },
  fetch_csv_catalog: {
    label: 'Fetched CSV catalog',
    icon: 'FileSpreadsheet',
    getDetail: (_input, output) => {
      const data = output?.data as Record<string, unknown> | undefined;
      const rows = data?.total_rows;
      return rows ? `${rows} rows` : '';
    },
  },
  batch_create_products: {
    label: 'Created product batch',
    icon: 'PackagePlus',
    getDetail: (_input, output) => {
      const data = output?.data as Record<string, unknown> | undefined;
      const count = data?.created_count;
      return count ? `${count} products` : '';
    },
  },
  send_supplier_email: {
    label: 'Sent supplier email',
    icon: 'Mail',
    getDetail: (input) => {
      const to = input?.to;
      const template = input?.template;
      return template ? `${template} to ${String(to || '').slice(0, 30)}` : `to ${String(to || '').slice(0, 40)}`;
    },
  },
  list_supplier_emails: {
    label: 'Listed supplier emails',
    icon: 'Inbox',
    getDetail: () => '',
  },
  read_supplier_email: {
    label: 'Read supplier email',
    icon: 'MailOpen',
    getDetail: () => '',
  },
  complete_task: {
    label: 'Completed task',
    icon: 'CheckCircle',
    getDetail: (input) => {
      const status = input?.status;
      const msg = input?.message;
      return msg ? `${status}: ${String(msg).slice(0, 80)}` : String(status || '');
    },
  },
  continue_task: {
    label: 'Continuing task',
    icon: 'ArrowRight',
    getDetail: (input) => {
      const reason = input?.reasoning;
      return reason ? String(reason).slice(0, 80) : '';
    },
  },
};

function titleCase(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface FormattedTool {
  label: string;
  detail: string;
  icon: string;
}

export function formatToolExecution(
  toolName: string,
  input?: Record<string, unknown> | null,
  output?: Record<string, unknown> | null
): FormattedTool {
  const mapping = TOOL_MAPPINGS[toolName];
  if (!mapping) {
    return {
      label: titleCase(toolName),
      detail: '',
      icon: 'Wrench',
    };
  }

  return {
    label: mapping.label,
    detail: mapping.getDetail(input, output),
    icon: mapping.icon,
  };
}
